import { Card, CircularProgress, Stack, Typography } from '@mui/material'
import { CommentParentType } from '../../../models/Comment'
import { Comments } from '../../customComponents/comments/Comments'
import { PlanOrganizationTaskName, PlanOrganizationTaskStatus, type PlanOrganizationTask } from '../../../models/PlanOrganizationTask'
import { useCallback, useEffect, useState } from 'react'
import { GetPlanOrganizationTaskSubsetForCurrentOrg } from '../../../services/AnnualPlanService'
import { PeerToPeerDataCollectionSystemTool } from './PeerToPeerDataCollectionSystemTool'
import { CoachingCapacityTool } from './CoachingCapacityTool'
import { FocusStudentDataTool } from './FocusStudentDataTool'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  annualPlanId: number
  rcnId: number
  showComments?: boolean
}
export const ImplementationDataTools = (props: Props): JSX.Element => {
  const [peerToPeerTask, setPeerToPeerTask] = useState<PlanOrganizationTask>()
  const [coachingCapacityTask, setCoachingCapacityTask] = useState<PlanOrganizationTask>()
  const [focusStudentDataBOYTask, setFocusStudentDataBOYTask] = useState<PlanOrganizationTask>()
  const [focusStudentDataEOYTask, setFocusStudentDataEOYTask] = useState<PlanOrganizationTask>()
  const [isLoading, setIsLoading] = useState(true)

  const fetchTask = useCallback(async () => {
    setIsLoading(true)
    const taskNamesToFetch = [
      PlanOrganizationTaskName.PeerToPeerDataCollectionSystem,
      PlanOrganizationTaskName.CoachingCapacityTool,
      PlanOrganizationTaskName.FocusStudentDataBOY,
      PlanOrganizationTaskName.FocusStudentDataEOY
    ]

    const tasks = await handleApiResponse(GetPlanOrganizationTaskSubsetForCurrentOrg(props.annualPlanId, taskNamesToFetch))
    if (tasks == null) return
    setPeerToPeerTask(tasks.find(t => t.taskName === PlanOrganizationTaskName.PeerToPeerDataCollectionSystem))
    setCoachingCapacityTask(tasks.find(t => t.taskName === PlanOrganizationTaskName.CoachingCapacityTool))
    setFocusStudentDataBOYTask(tasks.find(t => t.taskName === PlanOrganizationTaskName.FocusStudentDataBOY))
    setFocusStudentDataEOYTask(tasks.find(t => t.taskName === PlanOrganizationTaskName.FocusStudentDataEOY))

    setIsLoading(false)
  }, [props.annualPlanId, props.rcnId])

  useEffect(() => {
    void fetchTask()
  }, [fetchTask])

  if (isLoading) {
    return <CircularProgress />
  }

  if (peerToPeerTask == null ||
    coachingCapacityTask == null ||
    focusStudentDataBOYTask == null ||
    focusStudentDataEOYTask == null
  ) return <></>

  return (
    <Card
      data-testid='implementationDataTools'
      sx={{
        backgroundColor: 'var(--grey-100)',
        borderRadius: '16px',
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      <Stack gap='10px'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h3'>Data Tools</Typography>

          <Comments
            parentType={CommentParentType.PlanOrganizationTask}
            parentId={peerToPeerTask?.id}
            showComments={props.showComments}
          />
        </Stack>

        <Typography variant='body1'>
          The following data tools are completed outside of the START Planning and Data System (PDS). Please review the instructions, gather the data, and then mark the data tool as complete when 100% of forms have been completed.
        </Typography>
      </Stack>

      <Stack gap='10px'>
        <PeerToPeerDataCollectionSystemTool
          planOrganizationTaskId={peerToPeerTask.id}
          dueDate={peerToPeerTask.dueDate}
          completed={peerToPeerTask.status === PlanOrganizationTaskStatus.UpdatesSubmitted}
        />
        <CoachingCapacityTool
          planOrganizationTaskId={coachingCapacityTask.id}
          dueDate={coachingCapacityTask.dueDate}
          completed={coachingCapacityTask.status === PlanOrganizationTaskStatus.UpdatesSubmitted}
        />
        <FocusStudentDataTool
          planOrganizationTasks={[focusStudentDataBOYTask, focusStudentDataEOYTask]}
        />
      </Stack>
    </Card>
  )
}
